import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  createLocalizedPath,
  getInitialLanguage,
  languageDetails,
  translations,
  type Language,
  type TranslationDictionary
} from '@/shared/i18n';

type I18nProviderProps = {
  children: React.ReactNode;
};

type I18nProviderState = {
  content: TranslationDictionary;
  language: Language;
  locale: string;
  localizedPath: (path: string) => string;
  setLanguage: (language: Language) => void;
};

const storageKey = 'datasliv-language-preference';
const languageTransitionDurationMs = 760;
const languageSwapDelayMs = 380;

const I18nProviderContext = createContext<I18nProviderState | undefined>(
  undefined
);

const updateMetaContent = (
  attribute: 'name' | 'property',
  selectorValue: string,
  content: string
) => {
  const meta = document.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${selectorValue}"]`
  );

  if (meta) {
    meta.content = content;
  }
};

const shouldSkipLanguageTransition = () =>
  typeof window === 'undefined' ||
  (typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches);

export const I18nProvider = ({ children }: I18nProviderProps) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);
  const [isLanguageTransitioning, setIsLanguageTransitioning] = useState(false);
  const [languageTransitionKey, setLanguageTransitionKey] = useState(0);
  const languageRef = useRef(language);
  const languageSwapTimerRef = useRef<number | undefined>(undefined);
  const transitionEndTimerRef = useRef<number | undefined>(undefined);

  const clearTransitionTimers = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (languageSwapTimerRef.current !== undefined) {
      window.clearTimeout(languageSwapTimerRef.current);
      languageSwapTimerRef.current = undefined;
    }

    if (transitionEndTimerRef.current !== undefined) {
      window.clearTimeout(transitionEndTimerRef.current);
      transitionEndTimerRef.current = undefined;
    }
  }, []);

  const setLanguage = useCallback(
    (nextLanguage: Language) => {
      if (nextLanguage === languageRef.current) {
        return;
      }

      clearTransitionTimers();

      if (shouldSkipLanguageTransition()) {
        languageRef.current = nextLanguage;
        setLanguageState(nextLanguage);
        setIsLanguageTransitioning(false);
        return;
      }

      setIsLanguageTransitioning(true);
      setLanguageTransitionKey(currentKey => currentKey + 1);

      languageSwapTimerRef.current = window.setTimeout(() => {
        languageRef.current = nextLanguage;
        setLanguageState(nextLanguage);
        languageSwapTimerRef.current = undefined;
      }, languageSwapDelayMs);

      transitionEndTimerRef.current = window.setTimeout(() => {
        setIsLanguageTransitioning(false);
        transitionEndTimerRef.current = undefined;
      }, languageTransitionDurationMs);
    },
    [clearTransitionTimers]
  );

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => clearTransitionTimers, [clearTransitionTimers]);

  const localizedPath = useCallback(
    (path: string) => createLocalizedPath(path, language),
    [language]
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(language));
    } catch {
      // Language choice still works for the current session.
    }
  }, [language]);

  useEffect(() => {
    const languageMeta = languageDetails[language];
    const metadata = translations[language].meta;

    document.documentElement.lang = languageMeta.htmlLang;
    document.documentElement.dir = 'ltr';

    if (document.documentElement.dataset.pageSeo === 'custom') {
      return;
    }

    document.title = metadata.title;

    updateMetaContent('name', 'description', metadata.description);
    updateMetaContent('name', 'keywords', metadata.keywords);
    updateMetaContent('property', 'og:locale', languageMeta.ogLocale);
    updateMetaContent('property', 'og:title', metadata.title);
    updateMetaContent('property', 'og:description', metadata.ogDescription);
    updateMetaContent('property', 'og:image:alt', metadata.imageAlt);
    updateMetaContent('name', 'twitter:title', metadata.title);
    updateMetaContent(
      'name',
      'twitter:description',
      metadata.twitterDescription
    );
  }, [language]);

  const value = useMemo(
    () => ({
      content: translations[language],
      language,
      locale: languageDetails[language].locale,
      localizedPath,
      setLanguage
    }),
    [language, localizedPath, setLanguage]
  );

  return (
    <I18nProviderContext.Provider value={value}>
      {children}
      {isLanguageTransitioning && (
        <div
          key={languageTransitionKey}
          className="language-transition-overlay"
          aria-hidden="true"
        >
          <div className="language-transition-overlay__wash" />
        </div>
      )}
    </I18nProviderContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nProviderContext);

  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }

  return context;
};
